import React, { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FormikProps } from 'formik';
import moment, { Moment } from 'moment';

// utils
import { convertObjectToCamelCase } from 'utils/helpers';

// constants
import Rules from 'restrictions/roles/constants/rules';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// types
import { EventType } from 'pages/MerchantPages/Assets/InitialCreate/ContentType';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { Optional } from 'utils/types';

// actions
import {
  updateAssetEventType,
  updateAssetEventDetails,
  updateTemplateEventDetails,
  updateTemplateEventType,
} from 'ducks/merchantDucks/items/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';

// selectors
import { getItemDetails, getTemplateDetails } from 'ducks/merchantDucks/items/selectors';

// validation
import { validationSchema } from 'pages/MerchantPages/Assets/ContentDetails/containers/AssetContentDetails/validationSchema';

// components
import { CenterContent, ContentContainer, FullHeightForm } from 'components/SharedStyledElements';
import AccordionFooter from 'components/AccordionFooter';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';
import EventTypeDetails from 'pages/MerchantPages/Assets/ContentDetails/components/EventTypeDetails';
import ConfirmModal from 'components/Modals/ConfirmModal';
import Loader from 'components/Loader';

export interface FormValues {
  id: number;
  industry: string;
  contentCategory: string;
  eventName: string;
  webPage: string;
  eventDate: Optional<Moment>;
  // string if field is emptys
  expectedViewership: string | number;
  // string if field is emptys
  replayAvailable: string | boolean;
  isAdvertised: boolean;
  eventDescription?: string;
}

export interface EventDetails {
  contentCategory: string;
  eventDate: number;
  eventDescription: string;
  eventName: string;
  expectedViewership: number;
  id: number;
  industry: string;
  isAdvertised: boolean;
  itemId: number;
  merchantId: number;
  replayAvailable: boolean;
  webPage: string;
}

interface OwnProps {
  closePanel: () => void;
  handleSelectContentCategory: (category: string) => void;
  handleSetIsFormValid: (isValid: boolean) => void;
  isTemplate?: boolean;
  accordionLabel: string;
}

type Props = OwnProps;

const AssetContentDetails: FunctionComponent<Props> = ({
  closePanel,
  handleSelectContentCategory,
  handleSetIsFormValid,
  isTemplate = false,
  accordionLabel,
}) => {
  const dispatch = useDispatch();
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const itemDetails = useSelector(getItemDetails);
  const templateDetails = useSelector(getTemplateDetails);

  const { event_type: eventType, event_details: stateEventDetails, isSaving } = isTemplate
    ? templateDetails
    : itemDetails;

  const [category, setContentCategory] = useState(eventType);

  const eventDetails: EventDetails = convertObjectToCamelCase(stateEventDetails);

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    closePanel();
  };

  const onSubmitForm = async (data: FormValues) => {
    const { contentCategory } = data;
    setContentCategory(contentCategory);

    // If previosly saved content category is different from current one
    if (contentCategory !== eventType) {
      // If previously saved content category is Live (and now is changed to VOD or Other)
      if (eventType === EventType.LIVE) {
        dispatch(openModal(ModalNames.ChangeContentCategory));
        return;
      }

      if (contentCategory === EventType.LIVE) {
        // If previously saved content category is VOD or Other (and now is changed to Live)
        if (isTemplate) {
          dispatch(updateTemplateEventType(contentCategory, data));
          return;
        }
        dispatch(updateAssetEventType(contentCategory, data));
        return;
      }

      // VOD to Other or vice versa
      if (isTemplate) {
        dispatch(updateTemplateEventType(contentCategory));
        return;
      }
      dispatch(updateAssetEventType(contentCategory));
    }

    // If previously saved content category is not chaneged
    // and it is live, update only the event details object
    if (contentCategory === EventType.LIVE) {
      if (isTemplate) {
        dispatch(updateTemplateEventDetails(data));
        return;
      }
      dispatch(updateAssetEventDetails(data));
    }
  };

  const updateContentCategory = async () => {
    dispatch(updateAssetEventType(category));
  };

  const updateTemplateContentCategory = async () => {
    dispatch(updateTemplateEventType(category));
  };

  const {
    id,
    industry,
    contentCategory,
    eventName,
    webPage,
    eventDate,
    expectedViewership,
    replayAvailable,
    isAdvertised,
    eventDescription,
  } = eventDetails || {
    id: 0,
    industry: '',
    contentCategory: '',
    eventName: '',
    webPage: '',
    eventDate: null,
    expectedViewership: '',
    replayAvailable: '',
    isAdvertised: false,
    eventDescription: '',
  };

  const initialValues: FormValues = {
    id,
    industry,
    contentCategory: contentCategory !== '' ? contentCategory : eventType,
    eventName,
    webPage,
    eventDate: eventDate && eventDate > 0 ? moment.unix(eventDate) : null,
    expectedViewership: expectedViewership > 0 ? expectedViewership.toString() : '',
    // check if replayAvailable is not null or undefined, then convert
    // replayAvailable value to string because dropdown uses only string as values
    replayAvailable: replayAvailable !== null && replayAvailable !== undefined ? replayAvailable.toString() : '',
    isAdvertised,
    eventDescription,
  };

  if (isSaving) {
    return <Loader position="absolute" />;
  }

  return (
    <>
      <Formik<FormValues>
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onSubmitForm}
      >
        {({ resetForm, isValid, isSubmitting, dirty, values }: FormikProps<FormValues>) => {
          const disableContentTypeSaveButton = isReadOnly || !isValid || isSubmitting || !dirty;
          // used for show/hide the pulse info icon
          handleSetIsFormValid(isValid && dirty);

          return (
            <FullHeightForm>
              <FormAccordionHelper
                initialValues={initialValues}
                closePanel={closePanel}
                index={SectionType.ContentType}
              />
              <ContentContainer>
                <CenterContent>
                  <EventTypeDetails
                    isTemplate={isTemplate}
                    values={values}
                    handleSelectContentCategory={handleSelectContentCategory}
                  />
                </CenterContent>
                <AccordionFooter
                  accordionLabel={accordionLabel}
                  handleCancel={handleCancel(resetForm)}
                  disabled={disableContentTypeSaveButton}
                />
              </ContentContainer>
            </FullHeightForm>
          );
        }}
      </Formik>
      <ConfirmModal
        modalName={ModalNames.ChangeContentCategory}
        title="Change Content category"
        handleConfirm={isTemplate ? updateTemplateContentCategory : updateContentCategory}
        message="Are you sure you want to change the Content category?"
        description={`The live event information you have entered will be deleted.\nThe asset settings will not be affected in any way.`}
        buttonText="change"
        cancelText="Cancel"
      />
    </>
  );
};

export default AssetContentDetails;
